/**
 * useChessSocket - Socket.IO connection and event management for chess
 *
 * Handles:
 * - Socket connection lifecycle
 * - Event listeners (gameUpdated, joined, notify, error)
 * - Reconnection logic
 * - Emit helpers
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { Socket } from 'socket.io-client';
import io from 'socket.io-client';
import { Howl } from 'howler';
import Cookies from 'js-cookie';
import logger from '../../../utils/logger';
import {
    ChessGame,
    ChessGameSchema,
    ChessAuthData,
    Move,
    ClientToServerEvents,
    ServerToClientEvents
} from '../types';

// Sound instances
const basicSound = new Howl({
    src: ["/audio/correct.mp3"],
    autoplay: false,
    loop: false,
    volume: 0.5,
});

const winSound = new Howl({
    src: ["/audio/success.mp3"],
    autoplay: false,
    loop: false,
    volume: 0.5,
});

const wrongSound = new Howl({
    src: ["/audio/wrong_sound.mp3"],
    autoplay: false,
    loop: false,
    volume: 0.5,
});

type ChessSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export interface UseChessSocketOptions {
    gameId: string | undefined;
    serverUrl?: string;
    onGameUpdate?: (game: ChessGame) => void;
    onJoined?: (playerId: string) => void;
    onError?: (message: string) => void;
}

export interface UseChessSocketReturn {
    socket: ChessSocket | null;
    isConnected: boolean;
    game: ChessGame | null;
    playerId: string | null;
    joined: boolean;
    error: string | null;
    // Actions
    joinGame: (joinKey?: string) => void;
    makeMove: (move: Move) => void;
    resign: () => void;
    checkTime: () => void;
    viewGame: () => void;
}

// Cookie helpers
function getAuthData(gameId: string): ChessAuthData | null {
    const cookieKey = `ChessAuth-${gameId}`;
    const cookieData = Cookies.get(cookieKey);
    if (!cookieData) return null;
    try {
        return JSON.parse(cookieData) as ChessAuthData;
    } catch {
        return null;
    }
}

function storeAuthData(gameId: string, data: ChessAuthData): void {
    const cookieKey = `ChessAuth-${gameId}`;
    Cookies.set(cookieKey, JSON.stringify(data), { expires: 1 });
}

function clearAuthData(gameId: string): void {
    const cookieKey = `ChessAuth-${gameId}`;
    Cookies.remove(cookieKey);
}

export function useChessSocket({
    gameId,
    serverUrl = process.env.REACT_APP_SERVER_URL || '',
    onGameUpdate,
    onJoined,
    onError
}: UseChessSocketOptions): UseChessSocketReturn {
    const socketRef = useRef<ChessSocket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [game, setGame] = useState<ChessGame | null>(null);
    const [playerId, setPlayerId] = useState<string | null>(null);
    const [joined, setJoined] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Socket connection effect
    useEffect(() => {
        if (!gameId) return;

        logger.info("useChessSocket: connecting", { gameId });

        const newSocket: ChessSocket = io(`${serverUrl}/chess`, {
            reconnection: true,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 10000,
            reconnectionAttempts: 10,
            randomizationFactor: 0.5
        });

        socketRef.current = newSocket;

        // Connection handlers
        newSocket.on('connect', () => {
            logger.info("useChessSocket: connected");
            setIsConnected(true);
            setError(null);
            // View game on connect
            newSocket.emit("viewGame", gameId);
        });

        newSocket.on('disconnect', () => {
            logger.warn("useChessSocket: disconnected");
            setIsConnected(false);
        });

        // Game update handler
        const handleGameUpdate = (receivedGame: ChessGame) => {
            try {
                const validatedGame = ChessGameSchema.parse(receivedGame) as ChessGame;
                logger.debug("useChessSocket: game updated", { state: validatedGame.state });

                // Play sounds based on game state changes
                if (playerId) {
                    if (validatedGame.state === `${playerId}-wins`) {
                        winSound.play();
                    } else if (validatedGame.state.includes("-wins") && validatedGame.state !== `${playerId}-wins`) {
                        wrongSound.play();
                    }

                    // Store auth data
                    const currentPlayer = validatedGame.players.find(p => p.id === playerId);
                    if (currentPlayer) {
                        storeAuthData(gameId, {
                            playerId,
                            joinKey: currentPlayer.joinKey,
                            color: currentPlayer.color
                        });
                    }
                }

                setGame(validatedGame);
                onGameUpdate?.(validatedGame);
            } catch (err) {
                logger.error("useChessSocket: invalid game data", { error: err });
            }
        };

        // Joined handler
        const handleJoined = (receivedPlayerId: string) => {
            logger.info("useChessSocket: joined game", { playerId: receivedPlayerId });
            basicSound.play();
            setPlayerId(receivedPlayerId);
            setJoined(true);
            onJoined?.(receivedPlayerId);
        };

        // Notify handler
        const handleNotify = (text: string) => {
            basicSound.play();
            logger.debug("useChessSocket: notify", { text });
        };

        // Error handler
        const handleError = (text: string) => {
            wrongSound.play();
            logger.error("useChessSocket: error", { text });
            setError(text);
            onError?.(text);

            if (text.includes('join key')) {
                clearAuthData(gameId);
            }
        };

        // Register listeners
        newSocket.on("gameUpdated", handleGameUpdate);
        newSocket.on("joined", handleJoined);
        newSocket.on("notify", handleNotify);
        newSocket.on("error", handleError);

        // View game immediately
        newSocket.emit("viewGame", gameId);

        // Cleanup
        return () => {
            newSocket.off("gameUpdated", handleGameUpdate);
            newSocket.off("joined", handleJoined);
            newSocket.off("notify", handleNotify);
            newSocket.off("error", handleError);
            newSocket.disconnect();
            socketRef.current = null;
        };
    }, [gameId, serverUrl]);

    // Update playerId ref when it changes for sound effects
    useEffect(() => {
        // Re-register game update handler with new playerId
        // This is handled by the closure in the main effect
    }, [playerId]);

    // Actions
    const joinGame = useCallback((joinKey?: string) => {
        if (!socketRef.current || !gameId) return;
        logger.info("useChessSocket: joining game", { gameId, hasJoinKey: !!joinKey });
        socketRef.current.emit("joinGame", gameId, joinKey);
    }, [gameId]);

    const makeMove = useCallback((move: Move) => {
        if (!socketRef.current || !gameId) return;
        logger.debug("useChessSocket: making move", { gameId, move });
        socketRef.current.emit("makeMove", gameId, move);
    }, [gameId]);

    const resign = useCallback(() => {
        if (!socketRef.current || !gameId) return;
        logger.info("useChessSocket: resigning", { gameId });
        socketRef.current.emit("resign", gameId);
    }, [gameId]);

    const checkTime = useCallback(() => {
        if (!socketRef.current || !gameId) return;
        socketRef.current.emit("checkTime", gameId);
    }, [gameId]);

    const viewGame = useCallback(() => {
        if (!socketRef.current || !gameId) return;
        socketRef.current.emit("viewGame", gameId);
    }, [gameId]);

    return {
        socket: socketRef.current,
        isConnected,
        game,
        playerId,
        joined,
        error,
        joinGame,
        makeMove,
        resign,
        checkTime,
        viewGame
    };
}

// Export auth helpers for use elsewhere
export { getAuthData, storeAuthData, clearAuthData };
